/**
 * Delegação temporária de relação (T113/T132, DEL-01,
 * `ANALISE-CONTROLE-ACESSO.md` §4.8). Extraído de `compilador.ts` na T132/T143
 * para o hospedeiro caber no ratchet de 300 LOC (`docs/regras-migracao.md`
 * §4) — `compilarConcessao` continua o único chamador, e a forma pública
 * (`identidadesDe`, `type Delegacao`) é reexportada de lá para nenhum
 * importador existente precisar mudar (`compilador.spec.ts` importa os dois
 * de `"../compilador"`).
 *
 * **Delegação é identidade a mais na condição, não regra a mais.** A condição
 * de relação usa `$in: identidadesDe(usuario, agora, recurso)`: uma única
 * regra passa a conceder ao titular **e** ao substituto ativo, e nega a
 * terceiros — sem duplicar regra e sem o `$or` que este CASL nunca casa
 * (medido em `compilador.ts`, cabeçalho). **Delegação transfere relação,
 * nunca papel:** `identidadesDe` só troca quem conta como "o titular" para
 * efeito da condição; ela não inventa concessão nova. Se o substituto não tem
 * o papel que compila esta concessão em primeiro lugar, não há regra de
 * `action`/`subject` para a delegação anexar — a propriedade cai de graça do
 * desenho e está fixada em teste.
 *
 * A tabela de delegações é backend (`docs/contratos-backend.md` §9): este
 * módulo só consome `usuario.delegacoes`, trata lista ausente como vazia, e
 * reavalia a janela de vigência contra o `agora` que o chamador fornece —
 * nunca contra `Date.now()` direto, porque isso impediria o teste de fixar
 * uma delegação expirada.
 */
import type { Recurso } from "./catalogo/vocabulario";
import { CAMPO_ESCOPO } from "./catalogo/campo-escopo";

/**
 * O front nunca cria nem edita este registro — a tabela é backend, e o front só
 * consome o que a API devolve em `usuario.delegacoes`. `fim` é obrigatório: não
 * existe delegação indefinida.
 */
export interface Delegacao {
  id: string;
  /** E-mail do titular — de quem a relação está sendo emprestada. */
  de: string;
  /** E-mail do substituto — quem recebe a identidade extra. */
  para: string;
  /** ISO 8601. A janela é fechada no início. */
  inicio: string;
  /** ISO 8601. A janela é aberta no fim — `agora === fim` já está fora (§4.8). */
  fim: string;
  /** Delegação parcial: some recursos podem ficar de fora. */
  escopo?: {
    recursos?: readonly Recurso[];
    obras?: readonly string[];
  };
  criadoPor: string;
  motivo?: string;
}

/** O que este módulo precisa saber do usuário. Nada além disso. */
export interface IdentidadeDoUsuario {
  email: string;
  /**
   * Delegações que este usuário recebeu como substituto — recebidas e vigentes,
   * na forma como a API devolve (`docs/contratos-backend.md` §9). Lista ausente
   * ou vazia é o caso comum: ninguém emprestou relação a ninguém.
   */
  delegacoes?: readonly Delegacao[];
}

/**
 * As identidades que valem para a condição de relação: o próprio usuário, mais o
 * e-mail de todo titular que ativamente delegou a ele a relação deste `recurso`
 * (T113, DEL-01).
 *
 * Três filtros, todos precisam passar:
 * 1. **`para === usuario.email`** — só delegação recebida por ele conta; a dele
 *    própria como titular não aparece aqui (ele já é `usuario.email`).
 * 2. **Janela de vigência fechada no início, aberta no fim** — `inicio <= agora
 *    && agora < fim`, medido contra `agora`, nunca contra `Date.now()` direto: é
 *    o que permite ao teste fixar uma delegação expirada sem esperar o relógio.
 * 3. **Escopo de recurso** — sem `escopo.recursos` a delegação vale para todo
 *    recurso; com ele, só entra se `recurso` estiver na lista.
 *
 * **Continua devolvendo a lista PLANA de e-mails válidos** (T132): a
 * restrição por `escopo.obras` não muda "quem conta como titular" — muda a
 * REGRA que `compilarConcessao` compila para essas identidades, e por isso
 * mora em `gruposDeIdentidade`, abaixo, não aqui.
 */
/**
 * E-mail sem diferenciar caixa (RDO-04): Entra e Cognito podem divergir na
 * maiúscula, e a relação por e-mail compara sempre em minúsculas.
 */
export function emailNormalizado(email: string): string {
  return email.toLowerCase();
}

export function identidadesDe(
  usuario: IdentidadeDoUsuario,
  agora: Date,
  recurso: Recurso,
): string[] {
  const delegadas = delegacoesAtivas(usuario, agora, recurso).map((d) => emailNormalizado(d.de));

  return [...new Set([emailNormalizado(usuario.email), ...delegadas])];
}

/**
 * As delegações recebidas por `usuario`, vigentes agora.
 *
 * `recurso` opcional (T114, DEL-02): quando informado, filtra pelo escopo de
 * recurso da delegação (uso de `identidadesDe`/`gruposDeIdentidade`, que
 * decidem "esta concessão específica vale para o substituto?"); quando
 * omitido, devolve toda delegação vigente, sem filtrar por recurso — uso de
 * `menorFimDeDelegacaoAtiva`, que pergunta "a Ability inteira pode estar
 * desatualizada?", uma pergunta anterior a qualquer concessão específica.
 */
function delegacoesAtivas(
  usuario: IdentidadeDoUsuario,
  agora: Date,
  recurso?: Recurso,
): Delegacao[] {
  return (usuario.delegacoes ?? [])
    .filter((d) => emailNormalizado(d.para) === emailNormalizado(usuario.email))
    .filter((d) => new Date(d.inicio) <= agora && agora < new Date(d.fim))
    .filter((d) => !recurso || !d.escopo?.recursos || d.escopo.recursos.includes(recurso));
}

/**
 * Um grupo de identidades que compartilham a MESMA condição extra na regra de
 * relação — sem restrição de obra, ou restritas às mesmas obras (T132, DEL-01).
 *
 * Existe porque uma condição CASL é plana: para dar ao próprio usuário acesso
 * irrestrito e, na MESMA regra, restringir a identidade delegada a um
 * subconjunto de obras, seria preciso um `$or` que este CASL nunca casa. A
 * saída é a mesma que `exigeRelacao` já usa em `compilador.ts` — uma regra por
 * alternativa — só que a alternativa aqui é "grupo de identidade com sua
 * condição de obra", não "relação".
 */
export interface GrupoDeIdentidade {
  emails: string[];
  /** Mesclada em AND na regra deste grupo — hoje só o campo de obra, se houver. */
  condicaoExtra?: Record<string, unknown>;
}

/**
 * Particiona o próprio usuário e as delegações vigentes em grupos por condição
 * de obra (T132). Consumida só por `compilarConcessao` — `identidadesDe`
 * continua a pergunta plana "quem conta", inalterada.
 *
 * Recurso **sem entrada em `CAMPO_ESCOPO`** (`ProcessoJuridico`,
 * `TicketInteracao`) **não consegue honrar `escopo.obras`, e por isso fica
 * fail-closed**: a delegação restrita a obras simplesmente não concede
 * identidade nesses dois recursos — consistente com a invariante E4 que
 * `escopo.ts` já aplica no mesmo mapa, nunca conceder sem a restrição pedida.
 */
export function gruposDeIdentidade(
  usuario: IdentidadeDoUsuario,
  agora: Date,
  recurso: Recurso,
): GrupoDeIdentidade[] {
  const semRestricao = new Set([emailNormalizado(usuario.email)]);
  const comRestricao: GrupoDeIdentidade[] = [];

  for (const delegacao of delegacoesAtivas(usuario, agora, recurso)) {
    if (!delegacao.escopo?.obras || delegacao.escopo.obras.length === 0) {
      semRestricao.add(emailNormalizado(delegacao.de));
      continue;
    }

    const campoDeObra = CAMPO_ESCOPO[recurso]?.campo;
    // Fail-closed (E4, ADR-0004): sem campo de obra conhecido para este
    // recurso, a restricao nao tem como ser honrada — a delegacao nao concede
    // identidade aqui, em vez de conceder sem a restricao que ela pediu.
    if (!campoDeObra) continue;

    comRestricao.push({
      emails: [emailNormalizado(delegacao.de)],
      condicaoExtra: { [campoDeObra]: { $in: [...delegacao.escopo.obras] } },
    });
  }

  return [{ emails: [...semRestricao] }, ...comRestricao];
}

/**
 * A menor `fim` entre as delegações ATIVAS agora que `usuario` recebeu, sem
 * filtrar por recurso (T114, DEL-02, `src/store/user.ts`). `undefined` quando
 * não há nenhuma delegação vigente.
 *
 * **Não é o relógio de decisão.** O servidor reavalia a vigência a cada
 * request, contra o próprio relógio (B9.1, `docs/contratos-backend.md` §9) —
 * esta função só dá ao FRONT uma data para agendar `recarregarPermissoes()` e
 * a UI parar de mostrar um acesso que o backend já não concede mais. Não
 * decide autorização nenhuma; só lê a mesma janela vigência que
 * `delegacoesAtivas` já aplica.
 */
export function menorFimDeDelegacaoAtiva(
  usuario: IdentidadeDoUsuario,
  agora: Date,
): Date | undefined {
  const fins = delegacoesAtivas(usuario, agora).map((delegacao) => new Date(delegacao.fim));
  if (fins.length === 0) return undefined;
  return new Date(Math.min(...fins.map((data) => data.getTime())));
}
